import type {
  FilterProps,
  FilterRenderOptionsProps,
} from '@terminal/components/filter/Filter';
import { DynamicIncludeExcludeOptions } from '@terminal/components/filter/include-exclude/dynamic/DynamicIncludeExcludeOptions';
import type { FilterGroupKeys } from '@terminal/hooks/use-filter-actions/useFilterActions';

export type RenderFilterProps<T extends FilterGroupKeys> = Omit<
  FilterProps<T>,
  'labels' | 'addFilterOption' | 'removeFilterOption' | 'onCustomRangeClick'
>;

interface GetDynamicIncludeExcludeRenderOptionsProps<
  T extends FilterGroupKeys,
> {
  addFilterOption: FilterProps<T>['addFilterOption'];
  labels: FilterProps<T>['labels'];
  props: RenderFilterProps<T>;
}

export const getDynamicIncludeExcludeRenderOptions = <
  T extends FilterGroupKeys,
>({
  addFilterOption,
  labels,
  props,
}: GetDynamicIncludeExcludeRenderOptionsProps<T>): FilterProps<T>['renderOptions'] => {
  const includeExcludeLabels = labels.includeExcludeLabels;

  if (
    props.variant !== 'include-exclude-single-select' ||
    !includeExcludeLabels
  ) {
    return props.renderOptions;
  }

  const isMultiUseSingleFilter =
    (props.useType ?? 'single-use') === 'single-use' &&
    props.id !== props.filter.key;
  const initialSelectionMode = isMultiUseSingleFilter
    ? (props.filterOptionGroups?.[0]?.selectionMode ?? 'include')
    : 'include';

  return ({ options, showEmpty }: FilterRenderOptionsProps) => (
    <DynamicIncludeExcludeOptions
      addFilterOption={addFilterOption}
      disabledOptions={props.disabledOptions}
      filterId={props.id ?? props.filter.key}
      filterKey={props.filter.key}
      filterOptionGroups={props.filterOptionGroups ?? []}
      initialSelectionMode={initialSelectionMode}
      labels={includeExcludeLabels}
      noResultsLabel={labels.noResultsLabel}
      options={options}
      showEmpty={showEmpty}
    />
  );
};
